'use client';

import { useEffect, useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import CustomerCardPanel, { PIPELINE_COLUMNS, mapPipelineRow, type CustomerCard } from '@/components/common/CustomerCardPanel';

const NAVY = '#1C2E5E';
const ORANGE = '#E89A3C';

const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const stageTitle = (id: string) => PIPELINE_COLUMNS.find(c => c.id === id)?.title ?? id;
const stageColor = (id: string) => PIPELINE_COLUMNS.find(c => c.id === id)?.color ?? '#94A3B8';
const fullName = (p: any) => `${p.customer_first_name ?? ''} ${p.customer_last_name ?? ''}`.trim() || 'Customer';
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function GoalRing({ value, goal }: { value: number; goal: number }) {
  const pct = goal > 0 ? Math.min(1, value / goal) : 0;
  const r = 52, circ = 2 * Math.PI * r, off = circ * (1 - pct);
  const done = pct >= 1;
  return (
    <svg width="132" height="132" viewBox="0 0 132 132" className="flex-shrink-0">
      <circle cx="66" cy="66" r={r} fill="none" stroke="#EEF2F7" strokeWidth="12" />
      <circle cx="66" cy="66" r={r} fill="none" stroke={done ? '#10B981' : ORANGE} strokeWidth="12" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={off} transform="rotate(-90 66 66)" />
      <text x="66" y="62" textAnchor="middle" fontSize="24" fontWeight="800" fill={NAVY}>{Math.round(pct * 100)}%</text>
      <text x="66" y="82" textAnchor="middle" fontSize="10" fill="#9CA3AF">of goal</text>
    </svg>
  );
}

export default function SalesDashboardInteractive() {
  const { currentUser } = useAuth();
  const supabase = createClient();
  const myName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();

  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState<any[]>([]);
  const [estimates, setEstimates] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [goal, setGoal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [openCard, setOpenCard] = useState<CustomerCard | null>(null);
  const [loadingCard, setLoadingCard] = useState(false);

  // Date range filter
  const [range, setRange] = useState<'month' | 'quarter' | 'custom'>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Leaderboard (opt-in)
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState<{ name: string; total: number; deals: number }[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  useEffect(() => { setShowLeaderboard(typeof localStorage !== 'undefined' && localStorage.getItem('sales_leaderboard_optin') === '1'); }, []);
  const toggleLeaderboard = () => setShowLeaderboard(v => { const nv = !v; try { localStorage.setItem('sales_leaderboard_optin', nv ? '1' : '0'); } catch {} return nv; });

  const openCustomer = async (id: string) => {
    setLoadingCard(true);
    try {
      const { data } = await supabase.from('customer_pipeline').select('*').eq('id', id).limit(1);
      if (data?.[0]) setOpenCard(mapPipelineRow(data[0]));
    } finally {
      setLoadingCard(false);
    }
  };

  useEffect(() => {
    if (!myName) return;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Pipeline rows where I'm the Sales Rep (vpm_name) or Assigned.
        const { data: pl, error: e1 } = await supabase
          .from('customer_pipeline')
          .select('id, job_id, customer_first_name, customer_last_name, stage, contract_value, instant_estimate, vpm_name, assigned_to, job_scheduled_date, city, state, created_at, column_entered_at, updated_at')
          .or(`vpm_name.eq.${myName},assigned_to.eq.${myName}`)
          .abortSignal(controller.signal);
        if (e1) throw e1;
        const rows = pl || [];
        setPipelines(rows);
        const ids = rows.map((r: any) => r.id).filter(Boolean);
        const jobIds = rows.map((r: any) => r.job_id).filter(Boolean);

        await new Promise(r => setTimeout(r, 150));
        // 2. Estimates for my pipelines.
        if (ids.length) {
          const { data: ed } = await supabase
            .from('estimates')
            .select('id, pipeline_id, estimate_number, sent_at, signed_at, total')
            .in('pipeline_id', ids)
            .abortSignal(controller.signal);
          setEstimates(ed || []);
        } else setEstimates([]);

        await new Promise(r => setTimeout(r, 150));
        // 3. Invoices for my jobs.
        if (jobIds.length) {
          const { data: idata } = await supabase
            .from('invoices')
            .select('job_id, invoice_amount, invoice_status')
            .in('job_id', jobIds)
            .abortSignal(controller.signal);
          setInvoices(idata || []);
        } else setInvoices([]);

        await new Promise(r => setTimeout(r, 150));
        // 4. My monthly sales goal (from my employee record, matched by email).
        if (currentUser?.email) {
          const { data: emp } = await supabase
            .from('employees')
            .select('monthly_sales_goal')
            .eq('email', currentUser.email)
            .limit(1)
            .abortSignal(controller.signal);
          setGoal(emp?.[0]?.monthly_sales_goal ?? null);
        }
      } catch (e: any) {
        if (e?.name !== 'AbortError') setError(e?.message || 'Failed to load sales data.');
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myName, reloadKey]);

  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  const { rangeStart, rangeEnd, monthsInRange, rangeLabel } = useMemo(() => {
    const n = new Date();
    if (range === 'quarter') {
      const q = Math.floor(n.getMonth() / 3);
      return { rangeStart: new Date(n.getFullYear(), q * 3, 1), rangeEnd: new Date(n.getFullYear(), q * 3 + 3, 1), monthsInRange: 3, rangeLabel: 'This Quarter' };
    }
    if (range === 'custom') {
      const s = customStart ? new Date(customStart) : new Date(n.getFullYear(), n.getMonth(), 1);
      const e = customEnd ? new Date(new Date(customEnd).getTime() + 86400000) : new Date(n.getFullYear(), n.getMonth() + 1, 1);
      const months = Math.max(1, Math.round((e.getTime() - s.getTime()) / (86400000 * 30.44)));
      return { rangeStart: s, rangeEnd: e, monthsInRange: months, rangeLabel: 'Custom' };
    }
    return { rangeStart: new Date(n.getFullYear(), n.getMonth(), 1), rangeEnd: new Date(n.getFullYear(), n.getMonth() + 1, 1), monthsInRange: 1, rangeLabel: 'This Month' };
  }, [range, customStart, customEnd]);

  const inRange = (d?: string | null) => !!d && new Date(d) >= rangeStart && new Date(d) < rangeEnd;
  const daysSince = (d?: string | null) => (d ? Math.floor((now.getTime() - new Date(d).getTime()) / 86400000) : 0);

  const byStage = useMemo(() => {
    const m: Record<string, number> = {};
    pipelines.forEach(p => { m[p.stage] = (m[p.stage] || 0) + 1; });
    return PIPELINE_COLUMNS.map(c => ({ id: c.id, title: c.title, color: c.color, count: m[c.id] || 0 })).filter(s => s.count > 0);
  }, [pipelines]);

  const pendingEstimates = estimates.filter(e => e.sent_at && !e.signed_at);
  const signedInRange = estimates.filter(e => inRange(e.signed_at));
  const salesInRange = signedInRange.reduce((s, e) => s + Number(e.total || 0), 0);
  const rangeTarget = (goal || 0) * monthsInRange;

  const installsScheduled = pipelines.filter(p => p.job_scheduled_date && p.job_scheduled_date >= todayStr);
  const installsCompleted = pipelines.filter(p => p.job_scheduled_date && new Date(p.job_scheduled_date) < now && inRange(p.job_scheduled_date));

  // Conversion: of leads created in range, how many reached signed-contract.
  const leadsInRange = pipelines.filter(p => inRange(p.created_at));
  const signedAmongLeads = leadsInRange.filter(p => p.stage === 'signed-contract').length;
  const conversionPct = leadsInRange.length > 0 ? Math.round((signedAmongLeads / leadsInRange.length) * 100) : 0;

  // Follow-ups due
  const FOLLOWUP_DAYS = 4;
  const parkedStages = ['signed-contract', 'contact-at-later-date', 'unable-to-contact', 'overturn'];
  const pipeById: Record<string, any> = Object.fromEntries(pipelines.map(p => [p.id, p]));
  const staleFollowUps = pipelines
    .filter(p => !parkedStages.includes(p.stage) && daysSince(p.column_entered_at || p.updated_at || p.created_at) >= FOLLOWUP_DAYS)
    .map(p => ({ key: 's' + p.id, id: p.id, name: fullName(p), reason: `No movement in ${daysSince(p.column_entered_at || p.updated_at || p.created_at)} days`, tag: stageTitle(p.stage), days: daysSince(p.column_entered_at || p.updated_at || p.created_at) }));
  const estFollowUps = pendingEstimates
    .filter(e => daysSince(e.sent_at) >= 10)
    .map(e => { const p = pipeById[e.pipeline_id]; return p ? { key: 'e' + e.id, id: e.pipeline_id, name: fullName(p), reason: `Estimate sent ${daysSince(e.sent_at)} days ago, unsigned`, tag: 'Estimate aging', days: daysSince(e.sent_at) } : null; })
    .filter(Boolean) as { key: string; id: string; name: string; reason: string; tag: string; days: number }[];
  const followUps = [...staleFollowUps, ...estFollowUps].sort((a, b) => b.days - a.days).slice(0, 25);

  const paidByJob: Record<string, number> = {};
  invoices.forEach(i => { if ((i.invoice_status || '').toLowerCase() === 'paid') paidByJob[i.job_id] = (paidByJob[i.job_id] || 0) + Number(i.invoice_amount || 0); });
  const unpaid = pipelines
    .filter(p => Number(p.contract_value || 0) > 0 && (p.stage === 'signed-contract' || p.job_scheduled_date))
    .map(p => ({ ...p, paid: paidByJob[p.job_id] || 0, balance: Number(p.contract_value || 0) - (paidByJob[p.job_id] || 0) }))
    .filter(p => p.balance > 0.5)
    .sort((a, b) => b.balance - a.balance);

  const maxStage = Math.max(1, ...byStage.map(s => s.count));

  const kpis = [
    { label: 'In Pipeline', value: String(pipelines.length), icon: 'UsersIcon', tint: '#3B82F6', sub: 'assigned to me' },
    { label: 'Pending Estimates', value: String(pendingEstimates.length), icon: 'DocumentTextIcon', tint: '#8B5CF6', sub: 'awaiting signature' },
    { label: `Sales · ${rangeLabel}`, value: fmtMoney(salesInRange), icon: 'BanknotesIcon', tint: '#10B981', sub: `${signedInRange.length} signed` },
    { label: 'Conversion', value: `${conversionPct}%`, icon: 'ArrowTrendingUpIcon', tint: '#0EA5E9', sub: 'leads → signed' },
    { label: 'Installs Scheduled', value: String(installsScheduled.length), icon: 'CalendarDaysIcon', tint: '#E89A3C', sub: 'upcoming' },
    { label: 'Completed Installs', value: String(installsCompleted.length), icon: 'CheckBadgeIcon', tint: '#059669', sub: rangeLabel.toLowerCase() },
  ];

  // Leaderboard (opt-in): team-wide sales in range by sales rep.
  const loadLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    try {
      const { data: allPipes } = await supabase.from('customer_pipeline').select('id, vpm_name');
      const { data: allEst } = await supabase.from('estimates').select('pipeline_id, total, signed_at');
      const rep: Record<string, string> = {};
      (allPipes || []).forEach((p: any) => { if (p.vpm_name) rep[p.id] = p.vpm_name; });
      const totals: Record<string, { total: number; deals: number }> = {};
      (allEst || []).forEach((e: any) => {
        if (e.signed_at && new Date(e.signed_at) >= rangeStart && new Date(e.signed_at) < rangeEnd) {
          const r = rep[e.pipeline_id];
          if (r) { totals[r] = totals[r] || { total: 0, deals: 0 }; totals[r].total += Number(e.total || 0); totals[r].deals += 1; }
        }
      });
      setLeaderboard(Object.entries(totals).map(([name, v]) => ({ name, ...v })).sort((a, b) => b.total - a.total));
    } finally {
      setLeaderboardLoading(false);
    }
  }, [supabase, rangeStart, rangeEnd]);

  useEffect(() => { if (showLeaderboard) loadLeaderboard(); }, [showLeaderboard, loadLeaderboard]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold" style={{ color: NAVY }}>
            Sales Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {currentUser?.first_name ? `Welcome back, ${currentUser.first_name}. ` : ''}Your book of business at a glance.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {([['month', 'This Month'], ['quarter', 'This Quarter'], ['custom', 'Custom']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setRange(v)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${range === v ? 'text-white' : 'text-gray-500 hover:text-gray-700'}`} style={range === v ? { backgroundColor: NAVY } : {}}>{l}</button>
            ))}
          </div>
          {range === 'custom' && (
            <div className="flex items-center gap-1.5">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs" />
              <span className="text-gray-400 text-xs">to</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs" />
            </div>
          )}
          <Link href="/customer-dashboard" className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white shadow-sm" style={{ backgroundColor: NAVY }}>
            <Icon name="Squares2X2Icon" size={16} />
            <span className="hidden sm:inline">Open Sales Board</span>
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !myName ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-800">
          Your profile has no name set, so we can’t match jobs to you yet. Add your name in your profile and reload.
        </div>
      ) : (
        <>
          {/* Goal ring (scaled to the selected range) */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-5 flex-wrap">
            <GoalRing value={salesInRange} goal={rangeTarget} />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400">Sales Goal · {rangeLabel}</h3>
              {goal && goal > 0 ? (
                <>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    {fmtMoney(salesInRange)} <span className="text-sm font-medium text-gray-400">/ {fmtMoney(rangeTarget)}</span>
                  </p>
                  <p className={`text-sm mt-0.5 font-medium ${salesInRange >= rangeTarget ? 'text-green-600' : 'text-gray-500'}`}>
                    {salesInRange >= rangeTarget ? 'Goal reached — nice work! 🎉' : `${fmtMoney(rangeTarget - salesInRange)} to go`}
                  </p>
                  {monthsInRange > 1 && <p className="text-[11px] text-gray-400 mt-0.5">{fmtMoney(goal)}/mo × {monthsInRange} months</p>}
                </>
              ) : (
                <p className="text-sm text-gray-400 mt-1 max-w-sm">
                  No goal set yet. Set it in the <span className="font-medium text-gray-600">Employee Directory</span> → open your profile → <span className="font-medium text-gray-600">Monthly Sales Goal</span>.
                </p>
              )}
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {kpis.map(k => (
              <div key={k.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${k.tint}1A` }}>
                    <Icon name={k.icon as any} size={18} style={{ color: k.tint }} variant="solid" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-800">{k.value}</p>
                <p className="text-xs font-semibold text-gray-600 mt-0.5">{k.label}</p>
                <p className="text-[11px] text-gray-400">{k.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Pipeline by status */}
            <div className="lg:col-span-1 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: NAVY }}>
                <Icon name="FunnelIcon" size={18} className="text-blue-500" variant="solid" />
                Pipeline by Status
              </h3>
              {byStage.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No active pipeline.</p>
              ) : (
                <div className="space-y-2.5">
                  {byStage.map(s => (
                    <div key={s.id}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600 font-medium">{s.title}</span>
                        <span className="text-gray-400">{s.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(s.count / maxStage) * 100}%`, backgroundColor: s.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* My customers */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: NAVY }}>
                <Icon name="UsersIcon" size={18} className="text-blue-500" variant="solid" />
                My Customers <span className="text-xs font-normal text-gray-400">({pipelines.length})</span>
              </h3>
              {pipelines.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No customers assigned to you yet.</p>
              ) : (
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="py-2 px-1 font-medium">Customer</th>
                        <th className="py-2 px-1 font-medium">Status</th>
                        <th className="py-2 px-1 font-medium text-right">Contract</th>
                        <th className="py-2 px-1 font-medium">Install</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pipelines
                        .slice()
                        .sort((a, b) => Number(b.contract_value || 0) - Number(a.contract_value || 0))
                        .map(p => (
                          <tr key={p.id} onClick={() => openCustomer(p.id)} className="border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors">
                            <td className="py-2 px-1">
                              <p className="font-medium text-gray-800">{fullName(p)}</p>
                              <p className="text-[11px] text-gray-400">{[p.city, p.state].filter(Boolean).join(', ')}</p>
                            </td>
                            <td className="py-2 px-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white whitespace-nowrap" style={{ backgroundColor: stageColor(p.stage) }}>
                                {stageTitle(p.stage)}
                              </span>
                            </td>
                            <td className="py-2 px-1 text-right font-medium text-gray-700">{Number(p.contract_value || 0) > 0 ? fmtMoney(Number(p.contract_value)) : '—'}</td>
                            <td className="py-2 px-1 text-gray-500 whitespace-nowrap">{fmtDate(p.job_scheduled_date)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Outstanding balances */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: NAVY }}>
              <Icon name="ExclamationCircleIcon" size={18} className="text-amber-500" variant="solid" />
              Outstanding Balances <span className="text-xs font-normal text-gray-400">({unpaid.length})</span>
            </h3>
            {unpaid.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">No customers with an outstanding balance. 🎉</p>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 px-1 font-medium">Customer</th>
                      <th className="py-2 px-1 font-medium text-right">Contract</th>
                      <th className="py-2 px-1 font-medium text-right">Paid</th>
                      <th className="py-2 px-1 font-medium text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaid.map(p => (
                      <tr key={p.id} onClick={() => openCustomer(p.id)} className="border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors">
                        <td className="py-2 px-1">
                          <p className="font-medium text-gray-800">{fullName(p)}</p>
                          <p className="text-[11px] text-gray-400">{p.job_id}</p>
                        </td>
                        <td className="py-2 px-1 text-right text-gray-600">{fmtMoney(Number(p.contract_value || 0))}</td>
                        <td className="py-2 px-1 text-right text-gray-600">{fmtMoney(p.paid)}</td>
                        <td className="py-2 px-1 text-right font-semibold text-red-600">{fmtMoney(p.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Follow-ups due */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: NAVY }}>
              <Icon name="BellAlertIcon" size={18} className="text-amber-500" variant="solid" />
              Follow-ups Due <span className="text-xs font-normal text-gray-400">({followUps.length})</span>
            </h3>
            {followUps.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">You’re all caught up. 🎉</p>
            ) : (
              <div className="space-y-2">
                {followUps.map(f => (
                  <button key={f.key} onClick={() => openCustomer(f.id)} className="w-full flex items-center justify-between gap-3 text-left rounded-lg border border-gray-100 hover:bg-gray-50 px-3 py-2.5 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{f.name}</p>
                      <p className="text-[11px] text-gray-400">{f.reason}</p>
                    </div>
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 whitespace-nowrap flex-shrink-0">{f.tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Team leaderboard (opt-in) */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h3 className="font-semibold text-base flex items-center gap-2" style={{ color: NAVY }}>
                <Icon name="TrophyIcon" size={18} className="text-amber-500" variant="solid" />
                Team Leaderboard <span className="text-xs font-normal text-gray-400">· {rangeLabel}</span>
              </h3>
              <button onClick={toggleLeaderboard} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                {showLeaderboard ? 'Hide' : 'Show leaderboard'}
              </button>
            </div>
            {!showLeaderboard ? (
              <p className="text-sm text-gray-400">Opt in to see how the team ranks by signed sales this period.</p>
            ) : leaderboardLoading ? (
              <div className="flex justify-center py-6"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
            ) : leaderboard.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No signed sales in this period yet.</p>
            ) : (
              <div className="space-y-1.5">
                {leaderboard.map((r, i) => {
                  const isMe = r.name === myName;
                  const medal = ['🥇', '🥈', '🥉'][i];
                  return (
                    <div key={r.name} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${isMe ? 'bg-blue-50 border border-blue-200' : ''}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-6 text-center text-sm font-bold text-gray-400">{medal || i + 1}</span>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${isMe ? 'text-blue-700' : 'text-gray-800'}`}>{r.name}{isMe ? ' (you)' : ''}</p>
                          <p className="text-[11px] text-gray-400">{r.deals} deal{r.deals === 1 ? '' : 's'}</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-gray-800 flex-shrink-0">{fmtMoney(r.total)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {loadingCard && (
        <div className="fixed inset-0 z-[115] bg-black/30 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {openCard && (
        <CustomerCardPanel
          card={openCard}
          onClose={() => { setOpenCard(null); setReloadKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
